import React from 'react';
import PropTypes from 'prop-types';
import Translations from '../../../../../requests/apa/Translator';
import Assets from '../../../../../requests/apa/Asset';
import './_MatchDetailLineupsBench.scss';

/**
 * MatchDetailLineupsBench — Lista de suplentes
 *
 * Sección debajo del campo en Large/Plantilla (node 106:11182)
 *
 * Estructura por fila (Line 20 / Line 21 = separador superior):
 *   ┌─────────────────────────────────────────────────────────┐  ← Line 20/21 (2px #b5b5ff)
 *   │ [num] [nombre home]          [nombre away] [num]        │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Conversión px→rem: (px / 1.5) / 10
 *
 * Fila Figma (total 64px → 4.267rem):
 *   Line (top): 2px → 0.133rem  ← Line 20 / Line 21
 *   Padding top: 16px → 1.067rem
 *   Contenido: 32px → 2.133rem
 *   Padding bottom: 14px → 0.933rem
 *
 * Posiciones absolutas dentro de 1200px:
 *   Home → left: 1px (≈0), number 32px + gap 48px + name 268px
 *   Away → left: 853px (56.867rem), name 268px + gap 48px + number 32px
 *
 * Sin flex ni grid — usa position: absolute + inline-block.
 */

/**
 * Una fila completa con Line 20/21 (separador) arriba.
 * Combina el jugador home (izq) y away (der) en el mismo contenedor
 * para que la línea cruce los 1200px de ancho.
 *
 * Si home.subEvent o away.subEvent existen (vienen del mock), se renderiza
 * debajo de lineups-bench__home / lineups-bench__name--away el evento:
 *   [Cancha icon] [minute'] [name]   ← Frame 26081441 en Figma
 * y el row adquiere la clase --has-sub (altura 92px en lugar de 64px).
 */

var BenchRow = function (props) {

  var home = props.home;
  var away = props.away;
  var noBorder = props.noBorder;
  var hasSub = (home && home.subEvent) || (away && away.subEvent);
  var rowClass = 'lineups-bench__row'
    + (hasSub ? ' lineups-bench__row--has-sub' : '')
    + (noBorder ? ' lineups-bench__row--no-border' : '');
  return (
    <div className={rowClass}>
      {/* Line 20 / Line 21 — separador horizontal full-width */}
      <span className="lineups-bench__line" />

      {/* Contenido home — col izq 20% (num+gap+name+sub-event) | col der 80% (iconos) */}
      {home && (
        <span className="lineups-bench__home">
          {/* Col izquierda: número + gap + nombre + sub-event + gol */}
          <span className="lineups-bench__home-col lineups-bench__home-col--left">
            <span className="lineups-bench__number">{home.number}</span>
            <span className="lineups-bench__gap" />
            <span className="lineups-bench__name">{home.name}</span>
            {home.subEvent && (
              <span className="lineups-bench__sub-event lineups-bench__sub-event--home">
                <span className="lineups-bench__sub-icon">
                  <img className="lineups-bench__sub-img" src={Assets.get(home.subEvent.iconUrl)} alt="" />
                </span>
                <span className="lineups-bench__sub-min" style={home.subEvent.minuteColor ? { color: home.subEvent.minuteColor } : undefined}>{home.subEvent.minute}'</span>
                <span className="lineups-bench__sub-name">{home.subEvent.name}</span>
              </span>
            )}
          </span>
          {/* Col derecha: iconos */}
          <span className="lineups-bench__home-col lineups-bench__home-col--right">
            {home.iconUrls && Object.keys(home.iconUrls).map(function (key) {
              if (key === 'sub_in' || key === 'sub_out') return null;
              if (key !== 'goal') {
                return (
                  <span key={key} className="lineups-bench__icon lineups-bench__icon--home">
                    <img className="lineups-bench__icon-img" src={home.iconUrls[key]} alt={key} />
                  </span>
                );
              } else if (key === 'goal') {
                return [...Array(home.goals)].map(function(_, idx) {
                  return (
                    <span key={key + '-' + idx} className="lineups-bench__icon lineups-bench__icon--home">
                      <img className="lineups-bench__icon-img" src={home.iconUrls[key]} alt={key} />
                    </span>
                  );
                });
              }
            })}
          </span>
        </span>
      )}

      {/* Contenido away — col izq 20% (iconos) | col der 80% (name+gap+num+sub-event) */}
      {away && (
        <span className="lineups-bench__away">
          {/* Col izquierda: iconos */}
          <span className="lineups-bench__away-col lineups-bench__away-col--left">
            {away.iconUrls && Object.keys(away.iconUrls).map(function (key) {
              if (key === 'sub_in' || key === 'sub_out') return null;
              if (key !== 'goal') {
                return (
                  <span key={key} className="lineups-bench__icon lineups-bench__icon--away">
                    <img className="lineups-bench__icon-img" src={away.iconUrls[key]} alt={key} />
                  </span>
                );
              } else if (key === 'goal') {
                return [...Array(away.goals)].map(function(_, idx) {
                  return (
                    <span key={key + '-' + idx} className="lineups-bench__icon lineups-bench__icon--away">
                      <img className="lineups-bench__icon-img" src={away.iconUrls[key]} alt={key} />
                    </span>
                  );
                });
              }
            })}
          </span>
          {/* Col derecha: nombre + gap + número + sub-event + gol */}
          <span className="lineups-bench__away-col lineups-bench__away-col--right">
            <span className="lineups-bench__name lineups-bench__name--away">{away.name}</span>
            <span className="lineups-bench__gap" />
            <span className="lineups-bench__number">{away.number}</span>
            {away.subEvent && (
              <span className="lineups-bench__sub-event lineups-bench__sub-event--away">
                <span className="lineups-bench__sub-name lineups-bench__sub-name--away">{away.subEvent.name}</span>
                <span className="lineups-bench__sub-min" style={away.subEvent.minuteColor ? { color: away.subEvent.minuteColor } : undefined}>{away.subEvent.minute}'</span>
                <span className="lineups-bench__sub-icon">
                  <img className="lineups-bench__sub-img" src={Assets.get(away.subEvent.iconUrl)} alt="" />
                </span>
              </span>
            )}
          </span>
        </span>
      )}
    </div>
  );
};

BenchRow.propTypes = {
  home: PropTypes.shape({
    number: PropTypes.number,
    name: PropTypes.string,
    iconUrls: PropTypes.objectOf(PropTypes.string),
    goals: PropTypes.number,
    subEvent: PropTypes.shape({ minute: PropTypes.number, name: PropTypes.string, iconUrl: PropTypes.string })
  }),
  away: PropTypes.shape({
    number: PropTypes.number,
    name: PropTypes.string,
    iconUrls: PropTypes.objectOf(PropTypes.string),
    goals: PropTypes.number,
    subEvent: PropTypes.shape({ minute: PropTypes.number, name: PropTypes.string, iconUrl: PropTypes.string })
  })
};

BenchRow.defaultProps = {
  home: null,
  away: null
};

var MatchDetailLineupsBench = function (props) {
  var homeTeam = props.homeTeam;
  var awayTeam = props.awayTeam;
  var homeBench = homeTeam.starters || [];
  var awayBench = awayTeam.starters || [];
  var homeSubBench = homeTeam.bench || [];
  var awaySubBench = awayTeam.bench || [];
  var maxRows = Math.max(homeBench.length, awayBench.length);
  var maxSubRows = Math.max(homeSubBench.length, awaySubBench.length);
  var rows = [];
  for (var i = 0; i < maxRows; i++) { rows.push(i); }
  var subRows = [];
  for (var j = 0; j < maxSubRows; j++) { subRows.push(j); }

  return (
    <div className="lineups-bench">
      {rows.map(function (idx) {
        return (
          <BenchRow
            key={'bench-row-' + idx}
            home={homeBench[idx] || null}
            away={awayBench[idx] || null}
          />
        );
      })}
      {maxSubRows > 0 && (
        <span>
          {/* Large/Divisors/Resume — banner «Sustitutos» (node I106:11182;245:6776) */}
          <div className="lineups-bench__divider">
            <span className="lineups-bench__divider-text">{Translations.get('eventodeportivo_partidos_alineaciones_texto_titulo_sustitutos')}</span>
          </div>
          {subRows.map(function (idx) {
            return (
              <BenchRow
                key={'sub-row-' + idx}
                home={homeSubBench[idx] || null}
                away={awaySubBench[idx] || null}
                noBorder={idx === 0}
              />
            );
          })}
        </span>
      )}
    </div>
  );
};

MatchDetailLineupsBench.propTypes = {
  homeTeam: PropTypes.shape({
    bench: PropTypes.arrayOf(
      PropTypes.shape({
        number: PropTypes.number,
        name: PropTypes.string
      })
    ).isRequired
  }).isRequired,
  awayTeam: PropTypes.shape({
    bench: PropTypes.arrayOf(
      PropTypes.shape({
        number: PropTypes.number,
        name: PropTypes.string
      })
    ).isRequired
  }).isRequired
};

export default MatchDetailLineupsBench;



// WEBPACK FOOTER //
// ./src/containers/Mundial2026/FootballMatchDetail/Components/MatchDetailLineupsBench/MatchDetailLineupsBench.jsx